import React, { Component } from 'react'
import hoistNonReactStatic from 'hoist-non-react-statics'
import { WithMediaUploadProps, MediaFile } from '@deal/chat-firebase'
import {
  UploadMediaInput,
  UploadMediaComponent,
  PersistMediaComponent,
  PersistMediaInput,
  UploadMediaMutationVariables,
  UploadMediaMutation,
  PersistMediaMutation,
  PersistMediaMutationVariables
} from '#src/generated/graphql'
import { MutationFn } from 'react-apollo'

function getDisplayName(WrappedComponent: React.ComponentType<any>) {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component'
}

interface Props {
  uploadMedia: MutationFn<UploadMediaMutation, UploadMediaMutationVariables>
  persistMedia: MutationFn<PersistMediaMutation, PersistMediaMutationVariables>
}

function withMediaUpload<P>(
  WrappedComponent: React.ComponentType<P & WithMediaUploadProps>
): React.ComponentType<P> {
  class WithMediaUpload extends Component<P & Props> {
    public static displayName = `WithMediaUpload(${getDisplayName(WrappedComponent)})`
    public constructor(props: P & Props) {
      super(props)
      this.uploadMedia = this.uploadMedia.bind(this)
      this.persistMedia = this.persistMedia.bind(this)
    }

    public uploadMedia(file: File): Promise<MediaFile> {
      const input: UploadMediaInput = {
        // @ts-ignore
        file: file, // Our generated types do not understand file upload scalars
        persist: true
      }
      return this.props
        .uploadMedia({
          variables: {
            input: input
          }
        })
        .then(response => {
          if (response && response.data && response.data.uploadMedia.mediaFile) {
            return {
              mimeType: response.data.uploadMedia.mediaFile.mimeType,
              url: response.data.uploadMedia.mediaFile.url,
              id: response.data.uploadMedia.mediaFile.id
            }
          } else {
            throw new Error('error uploading')
          }
        })
    }

    public persistMedia(file: MediaFile): Promise<MediaFile> {
      const input: PersistMediaInput = {
        id: file.id
      }

      return this.props
        .persistMedia({
          variables: {
            input: input
          }
        })
        .then(response => {
          if (response && response.data && response.data.persistMedia.mediaFile) {
            return {
              mimeType: response.data.persistMedia.mediaFile.mimeType,
              url: response.data.persistMedia.mediaFile.url,
              id: response.data.persistMedia.mediaFile.id
            }
          } else {
            throw new Error('error persisting')
          }
        })
    }

    public render() {
      return (
        <WrappedComponent
          {...this.props}
          handleUploadMedia={this.uploadMedia}
          handlePersistMedia={this.persistMedia}
        />
      )
    }
  }

  const WithMediaUploadWrapper: React.SFC<P> = props => {
    return (
      <UploadMediaComponent>
        {uploadMedia => (
          <PersistMediaComponent>
            {persistMedia => (
              <WithMediaUpload {...props} persistMedia={persistMedia} uploadMedia={uploadMedia} />
            )}
          </PersistMediaComponent>
        )}
      </UploadMediaComponent>
    )
  }

  return hoistNonReactStatic<P, WithMediaUploadProps>(WithMediaUploadWrapper, WrappedComponent)
}

export { withMediaUpload }
